import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'reactstrap';
import { topupByAcceptBlue } from "@/services/program/transferMonies";
import { assert3DS } from "@/services/program/assert3DS";
import { flashError, useDispatch } from '@/shared/components/flash';
import AcceptedCardBrands from '@/shared/components/AcceptedCardBrands';
import { useAcceptBluePayment } from "@/containers/Manager/ManageAccount/hooks/useAcceptBluePayment";
import {formatFloatValue}  from "@/shared/helpers";

const TopUpModal = ({ shortfallDollars, shortfallPoints, isOpen, toggle, onSuccess, onProcessingStart, orgId, programId }) => {
  const dispatch = useDispatch();
  const ab = useAcceptBluePayment();
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState({
    first_name: '',
    last_name: '',
    name_on_card: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const fmt = (n) => `$${parseFloat(n).toFixed(2)}`;

  // Helper function to check if string contains only letters and spaces
  const isValidName = (value) => {
    // Allow letters, spaces, hyphens, and apostrophes but no numbers
    return /^[a-zA-Z\s'-]*$/.test(value);
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form values when modal opens
      setBilling({
        first_name: '',
        last_name: '',
        name_on_card: '',
        address: '',
        city: '',
        state: '',
        zip: '',
      });
      ab.mountContainer('#topup-accept-blue-container');
    } else {
      ab.destroy();
    }
    return () => {
      ab.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onBillingChange = (field) => (e) => {
    const value = e.target.value;

    // Validate first_name and last_name to reject numeric values
    if (field === 'first_name' || field === 'last_name' || field === 'name_on_card') {
      if (!isValidName(value)) {
        // Don't update the field if it contains invalid characters
        return;
      }
    }

    setBilling((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = billing.first_name.trim() && billing.last_name.trim();

  const onClickTopUp = async () => {
    if (!isFormValid) {
      flashError(dispatch, 'Please enter the cardholder first and last name.');
      return;
    }


    onProcessingStart?.();
    setLoading(true);
    try {
      const { nonce, expiryMonth, expiryYear } = await ab.getNonceToken();

      const paayBilling = {
        line1: billing.address || '',
        city: billing.city || '',
        state: billing.state || '',
        postCode: billing.zip || '',
        country: '840',
      };

      const tds = await ab.verify3DS({ amount: formatFloatValue(shortfallDollars), billing: paayBilling });

      if (!assert3DS(tds)) {
        flashError(dispatch, '3-D Secure authentication failed. Please try another card.');
        return;
      }

      const payload = {
        token: `nonce-${nonce}`,
        amount: formatFloatValue(shortfallDollars),
        expiry_month: parseInt(expiryMonth, 10),
        expiry_year: String(expiryYear).length === 2 ? 2000 + parseInt(expiryYear, 10) : parseInt(expiryYear, 10),
        first_name: billing.first_name,
        last_name: billing.last_name,
        name_on_card: billing.name_on_card || undefined,
        address: billing.address || undefined,
        city: billing.city || undefined,
        state: billing.state || undefined,
        zip: billing.zip || undefined,
        application_type: 'FE',
        '3d_secure': { eci: tds.eci, cavv: tds.authenticationValue, ds_trans_id: tds.dsTransId },
      };

      const resp = await topupByAcceptBlue(orgId, programId, payload);

      if (resp?.successful) {
        onSuccess(resp);
      } else {
        flashError(dispatch, resp?.errorMessage || 'Top-up payment failed. Please try again.');
      }
    } catch (error) {
      flashError(dispatch, error?.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="payment-modal" isOpen={isOpen} toggle={!loading ? toggle : undefined} size="lg">
      <div className="payment-modal__container">
        {loading && (
          <div className="payment-modal__processing-overlay">
            <Spinner color="primary" />
            <p>Processing payment…</p>
          </div>
        )}
    <div className="cc-confirm-modal__header">
          <div className="cc-confirm-modal__header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h3 className="cc-confirm-modal__title">Top Up Balance</h3>
            <p className="cc-confirm-modal__subtitle">Add points to your balance by credit card</p>
          </div>
          <button className="cc-confirm-modal__close" onClick={toggle} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
         <div className="cc-confirm-modal__body">
          <p className="cc-confirm-modal__note">
            Your card will be charged the exact shortfall amount. Points are credited to your balance once payment is confirmed.
          </p>

          <div className="cc-confirm-modal__breakdown">
            <div className="cc-confirm-modal__row">
              <span>Points shortfall</span>
              <span>{shortfallPoints?.toLocaleString()} pts</span>
            </div>
            <div className="cc-confirm-modal__row cc-confirm-modal__row--total">
              <span>Total charged to card</span>
              <strong>{fmt(shortfallDollars)}</strong>
            </div>
          </div>
        </div>
        <div className="payment-modal__columns">
          {/* Left Column: Card Details */}
          <div className="payment-modal__column payment-modal__column--card">
            <div className="payment-modal__section-header">
              <div className="payment-modal__section-icon payment-modal__section-icon--card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="payment-modal__section-title">Card Details</h3>
              <div className="payment-modal__secure-badge">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                </svg>
                <span>SECURE</span>
              </div>
            </div>

            <div className="payment-modal__card-fields">
              <AcceptedCardBrands />
              <p className="cc-confirm-modal__note cc-confirm-modal__note--warning">
                Mastercard is not supported. Please use a different card to continue.
              </p>

              {/* AcceptBlue iframe container */}
              <div className="payment-modal__accept-blue-container">
                <div id="topup-accept-blue-container"/>
                {!ab.ready && (
                  <div className="payment-modal__card-loading">Loading secure card entry…</div>
                )}
              </div>

              {/* Name on Card field below the iframe */}
              <div className="payment-modal__field">
                <label className="payment-modal__label">Name on Card</label>
                <input
                  type="text"
                  value={billing.name_on_card}
                  onChange={onBillingChange('name_on_card')}
                  className="payment-modal__input"
                />
              </div>

              {/* 3DS verification message */}
              {ab.challengeVisible && (
                <div className="payment-modal__card-loading">Verifying your card with your bank…</div>
              )}
            </div>
          </div>

          {/* Right Column: Billing Address */}
          <div className="payment-modal__column payment-modal__column--billing">
            <div className="payment-modal__section-header">
              <div className="payment-modal__section-icon payment-modal__section-icon--location">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="payment-modal__section-title">Billing Address</h3>
            </div>

            <div className="payment-modal__billing-fields">
              {/* First and Last Name row */}
              <div className="payment-modal__field-row">
                <div className="payment-modal__field payment-modal__field--half">
                  <label className="payment-modal__label">First Name</label>
                  <input
                    type="text"
                    value={billing.first_name}
                    onChange={onBillingChange('first_name')}
                    className="payment-modal__input"
                  />
                </div>
                <div className="payment-modal__field payment-modal__field--half">
                  <label className="payment-modal__label">Last Name</label>
                  <input
                    type="text"
                    value={billing.last_name}
                    onChange={onBillingChange('last_name')}
                    className="payment-modal__input"
                  />
                </div>
              </div>

              {/* Address field */}
              <div className="payment-modal__field">
                <label className="payment-modal__label">Address</label>
                <input
                  type="text"
                  value={billing.address}
                  onChange={onBillingChange('address')}
                  className="payment-modal__input"
                />
              </div>

              {/* City, State, Zip row */}
              <div className="payment-modal__field-row payment-modal__field-row--three">
                <div className="payment-modal__field payment-modal__field--city">
                  <label className="payment-modal__label">City</label>
                  <input
                    type="text"
                    value={billing.city}
                    onChange={onBillingChange('city')}
                    className="payment-modal__input"
                  />
                </div>
                <div className="payment-modal__field payment-modal__field--state">
                  <label className="payment-modal__label">State</label>
                  <input
                    type="text"
                    value={billing.state}
                    onChange={onBillingChange('state')}
                    className="payment-modal__input"
                  />
                </div>
                <div className="payment-modal__field payment-modal__field--zip">
                  <label className="payment-modal__label">Zip</label>
                  <input
                    type="text"
                    value={billing.zip}
                    onChange={onBillingChange('zip')}
                    className="payment-modal__input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Pay button */}
         <div className="cc-confirm-modal__footer">
          <button
            type="button"
            className="cc-confirm-modal__btn cc-confirm-modal__btn--cancel"
            onClick={toggle}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cc-confirm-modal__btn cc-confirm-modal__btn--confirm"
            onClick={onClickTopUp}
            disabled={loading || !ab.ready || !isFormValid}
          >
            {loading ? 'Processing…' : `Pay ${fmt(shortfallDollars)}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TopUpModal;
